import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Container,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext"; // still used for login API
import { useSchool } from "../../context/SchoolContext"; // 👈 bring in SchoolContext

export default function Login() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const { handleLogin } = useAuth();
  const { refreshSchoolData } = useSchool(); // 👈 get school refresher
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      setError("Please enter both username and password");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const loginResult = await handleLogin(formData);

      if (loginResult?.status === "success") {
        const token = loginResult?.token;
        const schoolStatus = loginResult?.data?.school_status;

        if (token) {
          // Save token
          localStorage.setItem("token", token);

          // 👇 Immediately refresh school details after login
          await refreshSchoolData();
        }

        console.log("Login Success:", loginResult);

        if (schoolStatus === "new_user") {
          navigate("/school-profile", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } else {
        setError(
          loginResult?.message || "Invalid credentials. Please try again."
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(error.response?.data?.message || "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <AppBar position="static" color="primary" elevation={2}>
        <Toolbar>
          <img
            src="/logo192.png"
            alt="Logo"
            style={{ width: 40, marginRight: 10 }}
          />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Dambaruu Learning Made Fun
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container
        maxWidth="sm"
        sx={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 5,
        }}
      >
        <Card sx={{ width: "100%", borderRadius: 3, boxShadow: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography variant="h5" fontWeight="bold">
                Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your credentials to continue
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={onSubmit} noValidate>
              <TextField
                label="User ID"
                name="username"
                fullWidth
                variant="outlined"
                value={formData.username}
                onChange={handleInputChange}
                margin="normal"
                required
                autoFocus
              />
              <TextField
                label="Password"
                name="password"
                type="password"
                fullWidth
                variant="outlined"
                value={formData.password}
                onChange={handleInputChange}
                margin="normal"
                required
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 3,
                  mb: 2,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  height: 48,
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: "center",
          bgcolor: "#f1f1f1",
          borderTop: "1px solid #ddd",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Dambaruu |{" "}
          <a
            href="/privacy"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            Privacy Policy
          </a>{" "}
          |{" "}
          <a href="/terms" style={{ textDecoration: "none", color: "inherit" }}>
            Terms of Service
          </a>
        </Typography>
      </Box>
    </Box>
  );
}
